import React, { createContext, useContext } from "react";
import { Box, Stack, Typography } from "@mui/material";

const ColumnsContext = createContext(3);

/** Responsive `gridColumn` for a field spanning `span` columns of its group. */
export const useGridSpan = (span: number | "full") => {
  const columns = useContext(ColumnsContext);
  if (span === "full") return "1 / -1";
  return {
    xs: "1 / -1",
    sm: `span ${Math.min(span, Math.min(columns, 2))}`,
    md: `span ${Math.min(span, columns)}`,
  };
};

interface FieldGroupProps {
  title?: string;
  /** Short line under the title. */
  description?: string;
  /** Columns on desktop (tablet uses up to 2, mobile 1). */
  columns?: number;
  /** Space between fields (theme spacing). */
  gap?: number;
  /** Extra content on the right of the title (e.g. a toolbar). */
  action?: React.ReactNode;
  children: React.ReactNode;
}

/** Titled block of fields inside a section, laid out on a grid. */
const FieldGroup: React.FC<FieldGroupProps> = ({
  title,
  description,
  columns = 3,
  gap = 1,
  action,
  children,
}) => (
  <Box>
    {(title || action) && (
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 1.25 }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title && (
            <Typography
              variant="overline"
              sx={{
                display: "block",
                lineHeight: 1.6,
                fontWeight: 700,
                letterSpacing: 0.8,
                color: "text.secondary",
              }}
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
    )}
    <ColumnsContext.Provider value={columns}>
      <Box
        sx={{
          display: "grid",
          gap,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
            md: `repeat(${columns}, minmax(0, 1fr))`,
          },
        }}
      >
        {children}
      </Box>
    </ColumnsContext.Provider>
  </Box>
);

export default FieldGroup;
